import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { Cherry, Palette, Citrus, Wheat, X } from 'lucide-react';
import { GLASS, GLASS_SM } from './glass';
import { GlassCracks } from './GlassCracks';

const COPY = {
    en: {
        eyebrow: 'Clean label',
        heading: 'No unnecessary extras.',
        philosophy: 'Every ingredient has to earn its place.',
        body: 'Flavour, colour and texture matter too, but they follow the same rule: it goes in only if it has a clear function and suits the formula. And when we do add something, we want it from a natural source.',
        supporting_label: 'Supporting ingredients',
        supporting: [
            { name: 'Natural cherry flavour', role: 'Taste', Icon: Cherry },
            { name: 'Beetroot extract', role: 'Natural colour', Icon: Palette },
            { name: 'Citric acid', role: 'Freshness', Icon: Citrus },
            { name: 'Chicory inulin', role: 'Carrier', Icon: Wheat },
        ],
        without_label: 'What we leave out',
        without: [
            { name: 'Melatonin', reason: 'A hormone, regulated as a medicine in several EU countries. We work with nutrients instead.' },
            { name: 'Sucralose', reason: 'Not a fit for something you take every evening, long term. Natural cherry flavour does the job.' },
            { name: 'Proprietary blends', reason: 'Blends hide doses. Every amount in Sleepstack is on the label.' },
            { name: 'All other additives', reason: 'Anti-caking agents, fillers, capsule shells, coatings, artificial colours and the like.' },
        ],
    },
    nl: {
        eyebrow: 'Clean label',
        heading: 'Geen onnodige toevoegingen.',
        philosophy: 'Elk ingrediënt moet een reden hebben om erin te zitten.',
        body: 'Smaak, kleur en textuur doen ertoe. Maar ook daarvoor geldt dezelfde regel: alleen toevoegen als het functioneel is en bij de formule past. Voegen we iets toe? Dan willen we dit uit natuurlijke bron hebben.',
        supporting_label: 'Ondersteunende ingrediënten',
        supporting: [
            { name: 'Natuurlijk kersenaroma', role: 'Smaak', Icon: Cherry },
            { name: 'Bietenrood-extract', role: 'Natuurlijke kleur', Icon: Palette },
            { name: 'Citroenzuur', role: 'Frisheid', Icon: Citrus },
            { name: 'Cichorei-inuline', role: 'Drager', Icon: Wheat },
        ],
        without_label: 'Wat we weglaten',
        without: [
            { name: 'Melatonine', reason: 'Melatonine is een hormoon en wordt in verschillende EU-landen als geneesmiddel gereguleerd. Wij werken met voedingsstoffen.' },
            { name: 'Sucralose', reason: 'Past niet bij een formule voor langdurig avondgebruik. Natuurlijk kersenaroma doet het werk.' },
            { name: 'Proprietary blends', reason: 'Een blend verbergt doseringen. Bij Sleepstack staat elke hoeveelheid op het etiket.' },
            { name: 'Alle andere hulpstoffen', reason: 'Zoals antiklontermiddelen, vulstoffen, capsulehulzen, coatings en kunstmatige kleurstoffen.' },
        ],
    },
};

export function CleanLabelSection() {
    const { lang } = useLanguage();
    const c = COPY[lang] || COPY.en;

    return (
        <section className="section-padding relative" id="clean-label" style={{ scrollMarginTop: '120px' }}>
            <div className="section-container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative mt-10 mb-8 max-w-2xl p-6 sm:p-8 lg:p-10 ${GLASS}`}
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary absolute -top-8 left-1">
                        <span className="w-8 h-px bg-primary" />
                        {c.eyebrow}
                    </div>
                    <h2 className="heading-lg text-foreground mb-3" data-testid="clean-label-heading">{c.heading}</h2>
                    <p className="font-heading text-xl md:text-2xl text-secondary-dark mb-4">{c.philosophy}</p>
                    <p className="text-base text-slate-700 leading-relaxed">{c.body}</p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                    {/* Supporting ingredients */}
                    <div data-testid="supporting-ingredients">
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-4">
                            {c.supporting_label}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {c.supporting.map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className={`flex items-center gap-3 p-4 ${GLASS_SM}`}
                                    data-testid={`supporting-item-${i}`}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                        <item.Icon size={18} strokeWidth={1.75} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-foreground leading-tight">{item.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{item.role}</div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* What we leave out */}
                    <div data-testid="leave-out-block">
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-4">
                            {c.without_label}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {c.without.map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 12 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    className={`relative overflow-hidden flex items-start gap-3 p-4 h-full ${GLASS_SM}`}
                                    data-testid={`leave-out-item-${i}`}
                                >
                                    <GlassCracks variant={i} />
                                    <div className="relative w-6 h-6 rounded-full bg-slate-200/80 text-slate-600 flex items-center justify-center shrink-0 mt-0.5">
                                        <X size={13} strokeWidth={2.5} />
                                    </div>
                                    <div className="relative">
                                        <div className="text-sm font-semibold text-foreground leading-tight">{item.name}</div>
                                        <p className="text-xs text-muted-foreground leading-snug mt-1">{item.reason}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
